/**
 * i18n configuration.
 *
 * Internationalization setup using i18next + react-i18next. Locale resources are
 * auto-registered from every JSON file under `../locales/<lang>/<namespace>.json`
 * via Vite's `import.meta.glob` (eager => resolved at build time, so init stays
 * synchronous and no React Suspense boundary is required).
 *
 * Base languages (en, es, fr, pt-BR) ship bundled. Additional AI-generated
 * languages and per-tenant overrides are layered on at runtime via
 * `addDynamicLanguage` / `applyTranslationOverrides` (see TranslationsProvider).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const localeModules = import.meta.glob('../locales/*/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;

type LocaleResources = Record<string, Record<string, Record<string, unknown>>>;

const resources: LocaleResources = {};
const namespaceSet = new Set<string>();

for (const [filePath, mod] of Object.entries(localeModules)) {
  const match = filePath.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lang, ns] = match;
  namespaceSet.add(ns);
  resources[lang] ??= {};
  resources[lang][ns] = mod.default;
}

/** Bundled base languages, derived from the locale directory structure. */
export const BASE_LANGUAGES = Object.keys(resources).sort();

/** All namespaces discovered from the bundled locale files. */
export const NAMESPACES = Array.from(namespaceSet).sort();

/**
 * Display names for languages. Base languages plus a catalog of additional
 * languages that can be AI-generated from the admin Languages page.
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  'pt-BR': 'Português (Brasil)',
  // Additional languages available for AI generation
  de: 'Deutsch (German)',
  it: 'Italiano (Italian)',
  pt: 'Português (Portugal)',
  ru: 'Русский (Russian)',
  zh: '中文 (Chinese)',
  ko: '한국어 (Korean)',
  ja: '日本語 (Japanese)',
  vi: 'Tiếng Việt (Vietnamese)',
  ar: 'العربية (Arabic)',
  hi: 'हिन्दी (Hindi)',
  tl: 'Tagalog',
  hmn: 'Hmoob (Hmong)',
  so: 'Soomaali (Somali)',
};

export function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] || code;
}

/** Flatten nested locale JSON into dot-notation keys (string leaves only). */
function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, newKey));
    } else if (typeof value === 'string') {
      result[newKey] = value;
    }
  }
  return result;
}

/**
 * Flat English source per namespace: `{ ns: { "dot.key": value } }`.
 * Used by the override editor to build its key list.
 */
export function getEnglishFlatSource(): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  const enResources = resources['en'] ?? {};
  for (const [namespace, bundle] of Object.entries(enResources)) {
    out[namespace] = flattenObject(bundle as Record<string, unknown>);
  }
  return out;
}

/**
 * Nested English source per namespace (for `POST /tenant/languages/generate`
 * `sourceTranslations`). Returns the raw bundled `en` resources.
 */
export function getEnglishNamespaceSource(): Record<string, Record<string, unknown>> {
  return (resources['en'] ?? {}) as Record<string, Record<string, unknown>>;
}

/**
 * Bundled (override-free) value for a `namespace` + dot-`key` in a given base
 * language. Reads the original bundled `resources` (i18next keeps its own
 * cloned store, so applying overrides at runtime never mutates this map),
 * which is what the override editor needs as the "default" reference. Returns
 * `undefined` for non-base/generated languages (the editor falls back to the
 * English source).
 */
export function getResourceValue(
  lang: string,
  namespace: string,
  key: string,
): string | undefined {
  const bundle = resources[lang]?.[namespace] as Record<string, unknown> | undefined;
  if (!bundle) return undefined;
  // Resolve a dot-path against the (possibly nested) bundle.
  const direct = bundle[key];
  if (typeof direct === 'string') return direct;
  let node: unknown = bundle;
  for (const segment of key.split('.')) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem('blueprint-lang') || 'en',
  fallbackLng: 'en',
  supportedLngs: [...BASE_LANGUAGES],
  defaultNS: 'common',
  ns: NAMESPACES,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

i18n.on('languageChanged', (lng) => {
  localStorage.setItem('blueprint-lang', lng);
});

/** Change the active language and persist it to localStorage. */
export function changeLanguage(lng: string): void {
  void i18n.changeLanguage(lng);
}

/** Get the current language code. */
export function getCurrentLanguage(): string {
  return i18n.language || 'en';
}

/**
 * Register a dynamically generated language (from tenant settings) by merging
 * its per-namespace translations into i18next at runtime.
 */
export function addDynamicLanguage(
  langCode: string,
  translations: Record<string, Record<string, string>>,
): void {
  Object.entries(translations).forEach(([namespace, strings]) => {
    i18n.addResourceBundle(langCode, namespace, strings, true, true);
  });

  const current = i18n.options.supportedLngs;
  if (Array.isArray(current) && !current.includes(langCode)) {
    i18n.options.supportedLngs = [...current, langCode];
  }
}

/** Keys that must never be used as object/namespace segments (proto pollution). */
const RESERVED_KEY_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Restore all bundled (base) languages to their original shipped resources,
 * discarding any previously applied overrides. Used by the overlay provider so
 * that re-applying the overlay is fully idempotent INCLUDING removals: a key
 * cleared in the admin editor disappears from the payload, and without this
 * reset the stale override would linger in i18next's store until a page reload.
 */
export function resetBundledLanguages(): void {
  for (const [langCode, namespaces] of Object.entries(resources)) {
    for (const [namespace, bundle] of Object.entries(namespaces)) {
      // deep=false => replace the namespace bundle wholesale (wipes overrides).
      i18n.addResourceBundle(langCode, namespace, bundle, false, true);
    }
  }
}

/**
 * Apply per-tenant translation overrides. Override keys use a flat
 * `namespace:dot.key` (or `namespace.dot.key`) format and patch specific keys
 * within an existing language. Reserved segments (`__proto__`, `constructor`,
 * `prototype`) are dropped to prevent prototype pollution, since these strings
 * are merged into objects in every visitor's browser via the public overlay.
 */
export function applyTranslationOverrides(
  overrides: Record<string, Record<string, string>>,
): void {
  Object.entries(overrides).forEach(([langCode, strings]) => {
    if (RESERVED_KEY_SEGMENTS.has(langCode)) return;
    const byNamespace: Record<string, Record<string, string>> = {};

    Object.entries(strings).forEach(([key, value]) => {
      const separator = key.includes(':') ? ':' : '.';
      const [namespace, ...rest] = key.split(separator);
      const actualKey = rest.join('.');
      if (!namespace || !actualKey) return;
      if (RESERVED_KEY_SEGMENTS.has(namespace) || rest.some((r) => RESERVED_KEY_SEGMENTS.has(r))) {
        return;
      }
      // Null-prototype object so a malicious `actualKey` cannot reach the proto.
      (byNamespace[namespace] ??= Object.create(null) as Record<string, string>)[actualKey] =
        value;
    });

    Object.entries(byNamespace).forEach(([namespace, nsStrings]) => {
      i18n.addResourceBundle(langCode, namespace, nsStrings, true, true);
    });
  });
}

export default i18n;
